import moment from 'moment-timezone';
import getPreciseDistance from 'geolib/es/getDistance';
import { defaultAgent, apiResponse } from '../../../helpers/web-agent';
import { toMilesPerHour, Unit } from '../../../helpers/unit';
import { secondsToTime, toDisplayTimeString, dateToUTC } from '../../../helpers/moment-ex';
import { EventEmitterX } from '../../../helpers/events';
import { RECORD_FOUND } from '../../../constants/messages';
import {
    SENSOR_CGPS, SENSOR_GPS, UNKNOWN_OPERATOR, SENSOR_PTO,
    SENSOR_BALE, API_DATE_FORMAT, API_TIME_FORMAT, DISPLAY_DATE_FORMAT2,
    LOAD_TYPE, DISPLAY_DATETIMESECONDS_FORMAT2,
    VIEW_EG,
    API_DATETIME_FORMAT
} from '../../../constants';
import { getCurrentView, groupBy, isAutomaticLoad } from '../../../helpers/common';

export const STREAM_FETCHING = "fetching";
export const STREAM_FETCHED = "fetched";
export const PTO_FETCHING = "pto_fetching";
export const PTO_FETCHED = "pto_fetched";
export const LOAD_FETCHING = "load_fetching";
export const LOAD_FETCHED = "load_fetched";
export const BALE_FETCHING = "bale_fetching";
export const BALE_FETCHED = "bale_fetched";
export const EVENT_PLAY = "play";
export const EVENT_PLAYING = "playing";
export const EVENT_PAUSE = "pause";
export const EVENT_STOP = "stop";
export const EVENT_LIVE_STREAM = "live_stream";
export const EVENT_CLEAR_STREAM = "clear_stream";
export const EVENT_KPI = "kpi_info";
export const EVENT_SPEED_VARIATION = "equipment_category_speedvariation";
export const SPEED_VARIATION_FETCHED = "speedvariation_fetched";
export const SPEED_VARIATION_FETCHING = "speedvariation_fetching";

export default class GPSPlayer extends EventEmitterX {
    constructor(map, google) {
        super();
        this.unit = Unit.getInstance();
        this.defaultOptions = { isShowMarker: true, markerLabel: 1 };
        this.sliceHours = 4;
        this.speed = 5;
        this.skipWhenEmpty = true;
        this.isEnableArea = false;
        this.isEnablePTO = false;
        this.isEnableLoad = false;
        this.isEnableBale = false;
        this.isEnableSpeedVariation = false;
        this.isEnableSeed = false;

        this.map = map;
        this.google = google;
        this.callbacks = {};
        this.timerId = null;
        this.egIdWiseList = {};
        this.gpsPoints = {};
        this.ptoSlot = {};
        this.loads = [];
        this.bales = {};
        this.mapObjects = {};
        this.playerStartTime = null;
        this.playerEndTime = null;
        this.leftDuration = 0;
        this.progressTime = null;
        this.isPlaying = false;
        this.options = { ...this.defaultOptions };
        this.loadWindowCount = 0;
        this.egCategorySpeedVariation = [];
        this.mapEGList = [];
        this.loadTs = [];
        this.regionId = null;
        this.loadNumberWithEgId = {};

        this.farmId = null;
        this.crewId = null;
        this.timezone = null;
        this.startDate = null;
        this.endDate = null;
        this.startUTCDate = null;
        this.endUTCDate = null;
        this.fieldDistance = []
    }

    request(params) {
        if (params.hasOwnProperty("farmId")) this.farmId = params.farmId;
        if (params.hasOwnProperty("crewId")) this.crewId = params.crewId;
        if (params.hasOwnProperty("regionId")) this.regionId = params.regionId;
        if (params.hasOwnProperty("timezone")) this.timezone = params.timezone;
        if (params.hasOwnProperty("startDate")) {
            this.startDate = moment.utc(params.startDate);
            this.startUTCDate = dateToUTC(params.startDate, this.timezone);
        }
        if (params.hasOwnProperty("endDate")) {
            this.endDate = moment.utc(params.endDate);
            this.endUTCDate = dateToUTC(params.endDate, this.timezone);
        }

        if (params.egList && Array.isArray(params.egList)) {
            this.egIdWiseList = {};
            params.egList.forEach(e => this.egIdWiseList[e.egId] = e);
        }

        return this;
    }

    stream() {
        try {
            return new Promise((resolve) => {
                if (!(this.startDate && this.endDate)) return this;
                if (Object.keys(this.egIdWiseList).length === 0) return this;

                this.emit(STREAM_FETCHING);
                const dateRange = { startTime: this.startDate.clone(), endTime: this.endDate.clone() };
                const utcDateRange = { startTime: this.startUTCDate.clone(), endTime: this.endUTCDate.clone() };

                const done = () => {
                    this.emit(STREAM_FETCHED, { start: this.playerStartTime, end: this.playerEndTime, progressTime: this.progressTime });
                    return resolve();
                };

                const baleRequest = () => {
                    if (this.isEnableBale) this.fetchBale(utcDateRange).then(done).catch(done);
                    else done();
                };

                const loadRequest = () => {
                    if (this.isEnableLoad) this.fetchLoadV1(dateRange).then(baleRequest).catch(baleRequest);
                    else baleRequest();
                };

                const ptoRequest = () => {
                    if (this.isEnablePTO) this.fetchPTO(utcDateRange).then(loadRequest).catch(loadRequest);
                    else loadRequest();
                };

                const speedVariationRequest = () => {
                    if (this.isEnableSpeedVariation) this.fetchSpeedVariation().then(ptoRequest).catch(ptoRequest);
                    else ptoRequest();
                };

                // Fetch GPS data from sensor API
                this.fetchGPS(utcDateRange).then(() => {
                    if (Object.keys(this.gpsPoints).length === 0) return done();

                    // Fetch PTO status from sensor API
                    speedVariationRequest();
                }).catch(ex => {
                    console.error(ex);
                    done();
                });

                // Fetch the completed acres from KPI
                if (this.isEnableArea) this.fetchKPI(dateRange);
            });
        } catch (ex) {
            throw ex;
        }
    }

    push({ egId, point, accuracy, ts, speed }) {
        if (!this.gpsPoints[egId]) this.gpsPoints[egId] = [];
        this.gpsPoints[egId].push({ egId, ts, point, accuracy, speed, polylineIndex: null, index: this.gpsPoints[egId].length });
        this.playerEndTime = ts;
        this.emit(EVENT_LIVE_STREAM, {
            start: this.playerStartTime,
            end: this.playerEndTime
        });
        return this;
    }

    play() {
        if (Object.keys(this.gpsPoints).length === 0) return this;
        if (this.leftDuration > 0 && this.leftDuration >= this.getTotalDuration()) {
            this.leftDuration = 0;
            this.clearMarkers();
            this.clearLoads();
            this.clearBales();
            this.clearPTOSwitchPoints();
            this.clearPaths();
        }
        this.isPlaying = true;
        this.emit(EVENT_PLAY);
        const next = this.getNextMovement();
        this.draw(next.start, next.end, (this.speed > 0));
        return this;
    }

    pause() {
        this.isPlaying = false;
        this.clearTimer();
        this.emit(EVENT_PAUSE, { leftDuration: this.leftDuration });
        return this;
    }

    stop() {
        this.isPlaying = false;
        this.clearTimer();
        this.emit(EVENT_STOP);
        return this;
    }

    skip(time) {
        const duration = this.playerStartTime ? (time - this.playerStartTime) : 0;
        if (duration === this.leftDuration) return this;

        if (duration > this.leftDuration) this.forward(duration);
        else this.backward(duration);
        return this;
    }

    forward(duration) {
        if (this.playerStartTime && duration > 0) {
            this.draw(
                (this.playerStartTime + this.leftDuration),
                (this.playerStartTime + duration), false);
        }
        return this;
    }

    backward(duration) {
        if (this.playerStartTime) {
            this.leftDuration = 0;
            this.clearMarkers();
            this.clearLoads();
            this.clearBales();
            this.clearPTOSwitchPoints();
            this.clearPaths();
            if (duration > 0) this.draw(this.playerStartTime, (this.playerStartTime + duration), false);
        }
        return this;
    }

    getNextMovement() {
        let start = this.playerStartTime, end = this.playerEndTime;
        if (this.speed > 0) {
            start = (this.playerStartTime + this.leftDuration);
            end = start + (this.speed * 1000);
            if (end > this.playerEndTime) end = this.playerEndTime;
        }
        return { start, end };
    }

    getTotalDuration() {
        if (this.playerStartTime && this.playerEndTime)
            return (this.playerEndTime - this.playerStartTime);
        else return 0;
    }

    setSpeed(speed) {
        this.speed = speed;
        return this;
    }

    draw(start, end, isRecursive = true) {
        this.clearTimer();

        const findPoints = this.getGPSPointsByTime(start, end);
        this.processPointsV1(findPoints);
        // if (this.isEnableLoad) this.putLoad(start, end);
        this.leftDuration += (end - start);
        this.progressTime = end;
        this.emit(EVENT_PLAYING, { leftDuration: this.leftDuration, progressTime: this.progressTime });

        if (this.leftDuration < this.getTotalDuration()) {
            if (isRecursive) {
                const timeOut = (this.skipWhenEmpty && findPoints.length === 0) ? 1 : 100;
                this.timerId = setTimeout(() => {
                    const next = this.getNextMovement();
                    if (next) this.draw(next.start, next.end);
                }, timeOut);
            } else this.pause();
        } else this.stop();

        return this;
    }

    getSpeedRange(point) {
        if (!this.isEnableSpeedVariation) return {};
        let range = point.speed || 0;

        const categoryId = (this.egIdWiseList[point.egId] || {}).categoryId;
        const currentCategorySpeedVariation = this.egCategorySpeedVariation.filter(v => v.equipment_category_id === categoryId);
        const maxThreshold = currentCategorySpeedVariation.reduce((prev, current) => (prev.speed_variation_range > current.speed_variation_range) ? prev : current);

        const speedRange = currentCategorySpeedVariation.find(v => (range > v.minSpeed && range <= v.maxSpeed)
            || (v.speed_variation_range === maxThreshold.speed_variation_range && range > v.minSpeed)
            || (v.speed_variation_range === 1 && range === 0));

        return speedRange || {};
    }

    getIsTruckLoad() {
        return (Object.keys(this.egIdWiseList).length === this._truckEGIds().length);
    }

    processPoints(points) {
        for (const curr of points) {
            if (!(this.gpsPoints && this.gpsPoints[curr.egId] && this.gpsPoints[curr.egId][curr.index])) continue;
            if (!this.mapObjects[curr.egId]) this.mapObjects[curr.egId] = { marker: null, polylines: [] };

            const egObjectItem = this.mapObjects[curr.egId];
            const point = new this.google.maps.LatLng(curr.point.lat, curr.point.lng);
            const prev = (curr.index > 0) ? this.gpsPoints[curr.egId][curr.index - 1] : null;
            const prevPoint = prev ? new this.google.maps.LatLng(prev.point.lat, prev.point.lng) : null;
            const path = prevPoint ? [prevPoint, point] : [point];

            if (curr.polylineIndex !== null && curr.polylineIndex <= (egObjectItem.polylines.length - 1)) {
                const polyline = egObjectItem.polylines[curr.polylineIndex].polyline;
                (curr.index > 0 && polyline.getPath().getArray().length > 0) ?
                    polyline.getPath().push(point) :
                    polyline.setPath(curr.connectPrev ? path : [point]);
                polyline.setMap(this.map);
            }
            else {
                const ptoSwitched = this.isPTOSwitched(prev, curr);
                const connectLine = (prev && prev.polylineIndex !== null) ? this.isConnectLine(prev, curr) : false;
                // get speed range
                const currSpeed = this.getSpeedRange(curr);
                const prevSpeed = prev ? this.getSpeedRange(prev) : null;

                const speedSwitched = (this.isEnableSpeedVariation && prevSpeed && currSpeed.speed_variation_range !== prevSpeed.speed_variation_range);
                let isNewLine = (!connectLine || ptoSwitched) || speedSwitched;
                let color = this.isEnableSpeedVariation ? currSpeed.color : this.egIdWiseList[curr.egId].color;

                if (this.isEnableSeed && this.loadTs.length > 1 && (this.loadTs[0].ts) < curr.ts) {
                    isNewLine = true;
                    color = this.loadTs && this.loadTs.length > 0 ? this.loadTs[0].color : '#0000';

                    this.loadTs = this.loadTs.filter((e, i) => i !== 0);
                }

                if (isNewLine) {
                    const connectPrev = connectLine && (ptoSwitched || speedSwitched);
                    // const color = this.isEnableSpeedVariation ? currSpeed.color : this.egIdWiseList[curr.egId].color;
                    const lineType = this.isEnablePTO && curr.hasOwnProperty("pto") ? (curr.pto ? 1 : 2) : 1;
                    egObjectItem.polylines.push(this.createPolylineItem({ lineType, color, path: connectPrev ? path : [point] }));
                    this.gpsPoints[curr.egId][curr.index].polylineIndex = (egObjectItem.polylines.length - 1);
                    if (connectPrev) this.gpsPoints[curr.egId][curr.index].connectPrev = connectPrev;
                } else if (prev.polylineIndex >= 0 && prev.polylineIndex <= (egObjectItem.polylines.length - 1)) {
                    const polyline = egObjectItem.polylines[prev.polylineIndex].polyline;
                    polyline.getPath().push(point);
                    polyline.setMap(this.map);
                    this.gpsPoints[curr.egId][curr.index].polylineIndex = prev.polylineIndex;
                }
            }

            if (this.options.isShowMarker) {
                if (!egObjectItem.marker) egObjectItem.marker = this.createMarker(this.egIdWiseList[curr.egId]);
                else if (egObjectItem.marker.getMap() === null) egObjectItem.marker.setMap(this.map);
                egObjectItem.marker.setPosition(point);
            }

            const isNonTruck = !this.egIdWiseList[curr.egId].isTruck;
            if (this.isEnablePTO && isNonTruck) this.putPTOSwitchPoint(prev, curr);
            if (this.isEnableBale && isNonTruck) this.putBale(curr.egId, curr);
        }
    }

    processPointsV1(points) {
        const batchLoads = {};
        const updatedMarkers = {};
        const batchSize = 4000;
        let currentIndex = 0;

        const overallStart = moment();

        const processBatch = () => {
            const endIndex = Math.min(currentIndex + batchSize, points.length);
            for (let i = currentIndex; i < endIndex; i++) {
                const curr = points[i];
                if (!this.isValidPoint(curr)) continue;
                this.initializeMapObject(curr.egId);

                const egObjectItem = this.mapObjects[curr.egId];
                const point = new this.google.maps.LatLng(curr.point.lat, curr.point.lng);
                const prev = curr.index > 0 ? this.gpsPoints[curr.egId][curr.index - 1] : null;

                if (this.isEnableLoad) this.collectBatchLoads(batchLoads, prev, curr);
                this.updatePolyline(egObjectItem, prev, curr, point);
                this.updateMarker(egObjectItem, curr, point, updatedMarkers);

                // PTO and Bale logic for non-trucks
                const isNonTruck = !this.egIdWiseList[curr.egId].isTruck;
                if (this.isEnablePTO && isNonTruck) this.putPTOSwitchPoint(prev, curr);
                if (this.isEnableBale && isNonTruck) this.putBale(curr.egId, curr);
            }

            currentIndex = endIndex;
            const isTruckLoad = this.getIsTruckLoad();
            if (currentIndex < points.length) {
                setTimeout(processBatch, 0);
            } else {
                Object.entries(batchLoads).forEach(([egId, loads]) => {
                    const egObjectItem = this.mapObjects[egId];
                    if (!egObjectItem) return;
                    if (!egObjectItem.loads) egObjectItem.loads = {};

                    loads.forEach(({ load, curr }) => {
                        const { loadId } = load;
                        const point = new this.google.maps.LatLng(curr.point.lat, curr.point.lng);

                        if (!egObjectItem.loads[loadId]) {
                            egObjectItem.loads[loadId] = { marker: null };
                        }

                        if (!isTruckLoad && this.egIdWiseList[curr.egId] && this.egIdWiseList[curr.egId].equipmentTypeId === 3) {
                            if (egObjectItem.loads[loadId] && egObjectItem.loads[loadId].marker) {
                                return;
                            }
                        }

                        let loadMarker = egObjectItem.loads[loadId].marker;

                        if (!loadMarker) {
                            loadMarker = this.createLoad(load);
                            egObjectItem.loads[loadId].marker = loadMarker;
                            loadMarker.setMap(this.map);
                        } else if (loadMarker.getMap() === null) {
                            loadMarker.setMap(this.map);
                        }

                        loadMarker.setPosition(point);
                    });
                });
                const overallEnd = moment();
                const totalTime = overallEnd.diff(overallStart, "seconds");
                console.log("🚀 ~ GPSPlayer ~ Total Processing Time ~ seconds:", totalTime);
                localStorage.setItem("totalProcessingTime", totalTime);
            }
        };
        processBatch();
    }

    isValidPoint(curr) {
        return this.gpsPoints && this.gpsPoints[curr.egId] && this.gpsPoints[curr.egId][curr.index];
    }

    initializeMapObject(egId) {
        if (!this.mapObjects[egId]) {
            this.mapObjects[egId] = { marker: null, polylines: [], loads: {} };
        }
    }

    collectBatchLoads(batchLoads, prev, curr) {
        if (!this.loads || this.loads.length === 0 || !curr) return;

        const loadList = this.loads
            .filter(load => this.isValidLoad(load, curr))
            .filter(load => this.isLoadInTimeRange(load, prev, curr));

        for (const load of loadList) {
            if (!batchLoads[load.egId]) batchLoads[load.egId] = [];
            batchLoads[load.egId].push({ load, curr });
        }
    }

    isValidLoad(load, curr) {
        const egIdData = this.egIdWiseList[curr.egId];
        if (!egIdData) return true;

        return egIdData.categoryId === 9 || load.transferType === 2
            ? curr.egId === load.sourceEGId
            : curr.egId === load.targetEGId;
    }

    isLoadInTimeRange(load, prev, curr) {
        return prev && curr ? load.ts >= prev.ts && load.ts <= curr.ts : load.ts <= curr.ts;
    }

    updatePolyline(egObjectItem, prev, curr, point) {
        const prevPoint = prev ? new this.google.maps.LatLng(prev.point.lat, prev.point.lng) : null;
        const path = prevPoint ? [prevPoint, point] : [point];

        if (curr.polylineIndex !== null && curr.polylineIndex < egObjectItem.polylines.length) {
            const polyline = egObjectItem.polylines[curr.polylineIndex].polyline;
            this.updatePolylinePath(polyline, point, path, curr.index > 0);
        } else {
            const isNewLine = this.shouldCreateNewPolyline(prev, curr);
            if (isNewLine) {
                const lineType = this.isEnablePTO && curr.hasOwnProperty("pto") ? (curr.pto ? 1 : 2) : 1;
                const color = this.getPolylineColor(prev, curr);
                egObjectItem.polylines.push(this.createPolylineItem({ lineType, color, path }));
                curr.polylineIndex = egObjectItem.polylines.length - 1;
            } else if (prev && prev.polylineIndex < egObjectItem.polylines.length) {
                const polyline = egObjectItem.polylines[prev.polylineIndex].polyline;
                polyline.getPath().push(point);
                curr.polylineIndex = prev.polylineIndex;
            }
        }
    }

    updatePolylinePath(polyline, point, path, connectPrev) {
        if (connectPrev && polyline.getPath().getArray().length > 0) {
            polyline.getPath().push(point);
        } else {
            polyline.setPath(connectPrev ? path : [point]);
        }
        polyline.setMap(this.map);
    }

    updateMarker(egObjectItem, curr, point, updatedMarkers) {
        if (this.options.isShowMarker) {
            if (!egObjectItem.marker) {
                egObjectItem.marker = this.createMarker(this.egIdWiseList[curr.egId]);
            } else if (egObjectItem.marker.getMap() === null) {
                egObjectItem.marker.setMap(this.map);
            }
            if (!updatedMarkers[curr.egId]) {
                egObjectItem.marker.setPosition(point);
                updatedMarkers[curr.egId] = true;
            }
        }
    }
    shouldCreateNewPolyline(prev, curr) {
        const ptoSwitched = this.isPTOSwitched(prev, curr);
        const connectLine = prev && prev.polylineIndex !== null ? this.isConnectLine(prev, curr) : false;
        const currSpeed = this.getSpeedRange(curr);
        const prevSpeed = prev ? this.getSpeedRange(prev) : null;
        const speedSwitched = this.isEnableSpeedVariation && prevSpeed && currSpeed.speed_variation_range !== prevSpeed.speed_variation_range;

        if (this.isEnableSeed && this.loadTs.length > 1 && this.loadTs[0].ts < curr.ts) {
            this.loadTs.shift();
            return true;
        }

        return !connectLine || ptoSwitched || speedSwitched;
    }

    getPolylineColor(prev, curr) {
        const currSpeed = this.getSpeedRange(curr);
        if (this.isEnableSpeedVariation && currSpeed) {
            return currSpeed.color;
        }
        else {
            return this.egIdWiseList[curr.egId] && this.egIdWiseList[curr.egId].color ? this.egIdWiseList[curr.egId].color : null;
        }
    }

    getGPSPointsByTime(start, end) {
        let findPoints = [];
        for (const egId in this.gpsPoints) {
            findPoints = findPoints.concat(this.gpsPoints[egId].filter(e => start <= e.ts && end >= e.ts));
        }
        findPoints.sort((a, b) => a.ts - b.ts);
        return findPoints;
    }

    putPTOSwitchPoint(prev, curr) {
        const egId = curr.egId;
        const egObjectItem = this.mapObjects[egId];
        if (!egObjectItem.ptoSwitchPoints) egObjectItem.ptoSwitchPoints = {};
        if (!egObjectItem.ptoSwitchPoints[curr.index]) egObjectItem.ptoSwitchPoints[curr.index] = { marker: null };

        let ptoPointMarker = egObjectItem.ptoSwitchPoints[curr.index].marker;
        if (!ptoPointMarker && this.isPTOSwitched(prev, curr)) {
            ptoPointMarker = this.createPTOSwitchPoint({ curr, prev, color: this.egIdWiseList[curr.egId].color });
            egObjectItem.ptoSwitchPoints[curr.index].marker = ptoPointMarker;
        }
        else if (ptoPointMarker && ptoPointMarker.getMap() === null) {
            ptoPointMarker.setMap(this.map);
        }
    }

    putBale(egId, gpsPoint) {
        if (!(gpsPoint.bale && this.bales.hasOwnProperty(gpsPoint.bale.baleId))) return;
        const point = new this.google.maps.LatLng(gpsPoint.point.lat, gpsPoint.point.lng);
        const egObjectItem = this.mapObjects[egId];
        if (!egObjectItem.bales) egObjectItem.bales = {};
        if (!egObjectItem.bales[gpsPoint.bale.baleId]) egObjectItem.bales[gpsPoint.bale.baleId] = { marker: null };

        let baleMarker = egObjectItem.bales[gpsPoint.bale.baleId].marker;
        if (!baleMarker) {
            const baleAttr = {
                ...this.egIdWiseList[egId],
                ...this.bales[gpsPoint.bale.baleId],
                ...gpsPoint.bale
            };
            baleMarker = this.createBale(baleAttr);
            egObjectItem.bales[gpsPoint.bale.baleId].marker = baleMarker;
        }
        else if (baleMarker.getMap() === null) baleMarker.setMap(this.map);
        baleMarker.setPosition(point);
    }

    putLoad(start, end) {
        if (!this.loads) return;

        const loadList = this.loads.filter(e => this.gpsPoints[e.egId] && e.location
            && start <= e.ts && end >= e.ts);
        for (const load of loadList) {
            const { egId, loadId, location } = load;
            const point = new this.google.maps.LatLng(location.lat, location.lng);

            const egObjectItem = this.mapObjects[egId];
            if (!egObjectItem.loads) egObjectItem.loads = {};
            if (!egObjectItem.loads[loadId]) egObjectItem.loads[loadId] = { marker: null };

            let loadMarker = egObjectItem.loads[loadId].marker;
            if (!loadMarker) {
                loadMarker = this.createLoad(load);
                egObjectItem.loads[loadId].marker = loadMarker;
            }
            else if (loadMarker.getMap() === null) loadMarker.setMap(this.map);
            loadMarker.setPosition(point);
        }
    }



    setBounds() {
        let bounds = null;
        for (const egId in this.gpsPoints) {
            for (const item of this.gpsPoints[egId]) {
                if (!bounds) bounds = new this.google.maps.LatLngBounds();
                const point = new this.google.maps.LatLng(item.point.lat, item.point.lng);
                bounds.extend(point);
            }
        }

        if (bounds) this.map.fitBounds(bounds);
        return this;
    }

    createPolylineItem(attr) {
        return { color: attr.color, lineType: attr.lineType, polyline: this.createPolyline(attr) };
    }

    createPolyline(attr) {
        return new this.google.maps.Polyline({
            map: this.map,
            path: attr.path,
            geodesic: true,
            icons: (attr.lineType === 2) ? [this._dottedLine()] : null,
            strokeOpacity: (attr.lineType === 2) ? 0 : 1,
            strokeColor: attr.color,
            strokeWeight: 4,
            editable: false,
            zIndex: 100
        });
    }

    _dottedLine() {
        return {
            icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                strokeWeight: 3,
                scale: 3
            },
            offset: '0',
            repeat: '15px'
        };
    }

    createPTOSwitchPoint(attr) {
        let title = null;
        const ptoIcon = {
            path: this.google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillOpacity: 1.0,
            strokeOpacity: 1.0,
            strokeWeight: 2.5
        };

        if (!attr.prev.pto && attr.curr.pto) {
            //title = "PTO OFF → ON";
            title = "PTO ON";
            ptoIcon.fillColor = "#28a745";
            ptoIcon.strokeColor = "#fff";
        } else {
            //title = "PTO ON → OFF";
            title = "PTO OFF";
            ptoIcon.fillColor = "#FF0000";
            ptoIcon.strokeColor = "#fff";
        }

        const marker = new this.google.maps.Marker({
            position: new this.google.maps.LatLng(attr.prev.point.lat, attr.prev.point.lng),
            map: this.map,
            title,
            icon: ptoIcon,
            zIndex: 999
        });

        return marker;
    }

    createMarker(attr) {
        const me = this;

        const title = (this.options.markerLabel === 2) ? (attr.operatorName || UNKNOWN_OPERATOR) : attr.name;
        const marker = new this.google.maps.Marker({
            map: this.map,
            title,
            icon: {
                url: attr.iconUrl,
                anchor: new this.google.maps.Point(32, 32),
                scaledSize: new this.google.maps.Size(42, 42)
            },
            zIndex: 9999
        });

        // infowindow
        const contentString = `<div id='infowindow_replay_${attr.egId}' class='info-active-replay map-popup_text' style='background:${attr.color}'>${title}</div>`;
        const infowindow = new this.google.maps.InfoWindow({
            content: contentString,
            disableAutoPan: true,
            zIndex: 9999
        });

        infowindow.open(this.map, marker);
        infowindow.addListener('domready', function () {
            const mapPopupContents = document.getElementsByClassName("map-popup_text");
            const parentEle = document.getElementsByClassName("gm-style-iw");
            if (mapPopupContents.length > 0) {
                for (const element of parentEle) {
                    element.classList.add("map-popup_container");
                }
            }
            me.hideInfoCloseButton(attr.egId);
        });

        return marker;
    }

    createLoad(load) {
        if (!this.egIdWiseList[load.egId]) return null;
        const me = this;
        const isEgLevel = getCurrentView() === VIEW_EG;
        const isSourceHarvestEg = load.transferType === 1;
        const egDetail = this.egIdWiseList[load.egId];
        // const loadDetail = this.loadNumberWithEgId[load.egId]
        let loadIcon = {
            path: this.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            strokeWeight: 2,
            fillColor: (this.isEnableSeed && load && load.pathColor) ? load.pathColor : egDetail.color,
            fillOpacity: 1.0,
            strokeColor: "white"
        };

        const squareSymbol = {
            path: 'M -10,-10 L 10,-10 L 10,10 L -10,10 Z',
            fillColor: (this.isEnableSeed && load && load.pathColor) ? load.pathColor : egDetail.color,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 1
        };

        if (!load.fieldShapeName) {
            loadIcon = {
                ...loadIcon,
                scale: 13,
                strokeWeight: 3,
                strokeColor: "black",
            };
        }

        const marker = new this.google.maps.Marker({
            map: this.map,
            title: egDetail.name,
            icon: isSourceHarvestEg ? loadIcon : isEgLevel ? loadIcon : squareSymbol,
            label: {
                text: load.loadNo.toString(),
                color: "white",
                fontSize: "12px",
                fontWeight: "bold"
            },
            zIndex: 999
        });

        this.google.maps.event.addListener(marker, 'click', () => {
            // infowindow
            const startTime = load.startTime ? moment.utc(load.startTime).tz(this.timezone) : '';
            const endTime = load.endTime ? moment.utc(load.endTime).tz(this.timezone) : '';
            let loadDate = startTime ? startTime.clone().format(DISPLAY_DATE_FORMAT2) : '';
            const duration = load.loadSeconds ? toDisplayTimeString(secondsToTime(load.loadSeconds, true)) : '';
            const loadWeight = this.unit.toRoundOfWeight(load.loadWeight);
            const anonymousTruck = load.anonymousEG ? "<b>*</b>" : "";
            const startAndEndTime = (startTime && endTime) ? `${startTime.format("hh:mm:ss a")} to ${endTime.format("hh:mm:ss a")}` : '';
            if (!loadDate) {
                loadDate = load.load_date ? moment.utc(load.load_date).tz(this.timezone).format(DISPLAY_DATETIMESECONDS_FORMAT2) : ''
            }
            const planterLoadDate = (load && load.isSequenceLoad && load.load_date)
                ? moment.utc(load.load_date).tz(this.timezone).format(DISPLAY_DATETIMESECONDS_FORMAT2) : '';

            const contentString = (load && load.isSequenceLoad) ? `<div class='info-load'>
            <table/>
                <tr><td><span>Crew</span></td> <td>:</td> <td><label>${load.crewName ? load.crewName : "N/A"}</label></td></tr>
                <tr><td><span>Load Origin</span></td> <td>:</td> <td><label>${load.sourceEGName ? load.sourceEGName : "N/A"}</label>${anonymousTruck}</td></tr>
                <tr><td><span>Implement</span></td> <td>:</td> <td><label>${load.targetEGName ? load.targetEGName : "N/A"}</label></td></tr>               
                ${planterLoadDate ? `<tr><td><span>Load Date & Time</span></td> <td>:</td> <td><label>${planterLoadDate ? planterLoadDate : ''}</label></td></tr>` : ''}                
                <tr><td><span>Weight</span></td> <td>:</td> <td><label>${loadWeight + ' ' + this.options.weightShortName}</label></td></tr>
                <tr><td><span>Field</span></td> <td>:</td> <td><label>${load.fieldShapeName || "N/A"}</label></td></tr>
            </table></div>`
                : `<div class='info-load'>
            <table/>
                <tr><td><span>Crew</span></td> <td>:</td> <td><label>${load.crewName ? load.crewName : "N/A"}</label></td></tr>
                <tr><td><span>Implement</span></td> <td>:</td> <td><label>${load.sourceEGName ? load.sourceEGName : "N/A"}</label></td></tr>
                <tr><td><span>Transport</span></td> <td>:</td> <td><label>${load.targetEGName ? load.targetEGName : "N/A"}</label>${anonymousTruck}</td></tr>
                ${loadDate ? `<tr><td><span>Load Date</span></td> <td>:</td> <td><label>${loadDate}</label></td></tr>` : ""}
                ${startAndEndTime ? `<tr><td><span>Load Time</span></td> <td>:</td> <td><label>${startAndEndTime}</label></td></tr>` : ""}
                ${duration ? `<tr><td><span>Duration</span></td> <td>:</td> <td><label>${duration}</label></td></tr>` : ""}
                <tr><td><span>Weight</span></td> <td>:</td> <td><label>${loadWeight + ' ' + this.options.weightShortName}</label></td></tr>
                <tr><td><span>Field</span></td> <td>:</td> <td><label>${load.fieldShapeName || "N/A"}</label></td></tr>
                ${load.predictedDistance ? `<tr><td><span>Estimated Distance</span></td> <td>:</td> <td><label>${load.predictedDistance} miles</label></td></tr>` : ""}
                ${load.actualDistance ? `<tr><td><span>Travelled Distance</span></td> <td>:</td> <td><label>${load.actualDistance} miles</label></td></tr>` : ""}
            </table>
        </div>`;
            const infowindow = new this.google.maps.InfoWindow({
                content: contentString,
                disableAutoPan: true,
                zIndex: 999
            });

            infowindow.addListener("closeclick", function () {
                marker.setZIndex(999);
                infowindow.setZIndex(999);
            });

            me.loadWindowCount++;
            marker.setZIndex(99999 + me.loadWindowCount);
            infowindow.setZIndex(99999 + me.loadWindowCount);
            infowindow.open(me.map, marker);
        });

        return marker;
    }

    createBale(attr) {
        const me = this;
        const baleIcon = {
            path: 'M24-8c0 4.4-3.6 8-8 8h-32c-4.4 0-8-3.6-8-8v-32c0-4.4 3.6-8 8-8h32c4.4 0 8 3.6 8 8v32z',
            scale: 0.45,
            strokeWeight: 2,
            fillColor: attr.color,
            fillOpacity: 1.0,
            strokeColor: "white",
            labelOrigin: new this.google.maps.Point(0, -23),
        };

        const marker = new this.google.maps.Marker({
            map: this.map,
            title: attr.name,
            icon: baleIcon,
            label: {
                text: attr.baleNo.toString(),
                color: "white",
                fontSize: "12px",
                fontWeight: "bold"
            },
            zIndex: 999
        });

        const formatDate = (ts) => ts ? moment.utc(ts).tz(me.timezone).format("hh:mm:ss A") : "";

        // infowindow
        const contentString = `<div class='info-load'>
            <table/>
                <tr><td><span>Implement</span></td> <td>:</td> <td><label>${attr.name}</label></td></tr>
                <tr><td><span>Flake Count</span></td> <td>:</td> <td><label>${attr.flakeCount}</label></td></tr>
                <tr><td><span>Weight</span></td> <td>:</td> <td><label>${attr.baleWeight === null ? "N/A" : attr.baleWeight + ' ' + this.options.weightShortName}</label></td></tr>
                <tr><td><span>Start Time</span></td> <td>:</td> <td><label>${formatDate(attr.baleStartTS)}</label></td></tr>
                <tr><td><span>End Time</span></td> <td>:</td> <td><label>${formatDate(attr.baleFinishedTS)}</label></td></tr>
                <tr><td><span>Drop Begin Time</span></td> <td>:</td> <td><label>${formatDate(attr.baleDropBeginTS)}</label></td></tr>
            </table>
        </div>`;
        const infowindow = new this.google.maps.InfoWindow({
            content: contentString,
            disableAutoPan: true,
            zIndex: 999
        });

        this.google.maps.event.addListener(marker, 'click', () => {
            me.baleWindowCount++;
            marker.setZIndex(99999 + me.baleWindowCount);
            infowindow.setZIndex(99999 + me.baleWindowCount);
            infowindow.open(me.map, marker);
        });

        infowindow.addListener("closeclick", function () {
            marker.setZIndex(999);
            infowindow.setZIndex(999);
        });

        return marker;
    }

    hideInfoCloseButton(egIds) {
        egIds = !Array.isArray(egIds) ? [egIds] : egIds;
        for (const egId of egIds) {
            const element = document.getElementById(`infowindow_replay_${egId}`);
            if (!element) continue;

            const parent = element.parentElement.parentElement.parentElement;
            if (!parent) continue;

            const childerns = parent.getElementsByClassName("gm-ui-hover-effect");
            if (childerns.length === 0) continue;
            childerns[0].style.display = 'none';
        }
    }

    isPTOSwitched(p1, p2) {
        if (p1 && p2 && p1.hasOwnProperty("pto") && p2.hasOwnProperty("pto") && p1.pto !== p2.pto) return true;
        return false;
    }

    isConnectLine(p1, p2) {
        const getSeconds = () => ((p1.ts && p2.ts) ? (p2.ts - p1.ts) / 1000 : 0);

        const getDistance = () => getPreciseDistance(
            { latitude: p1.point.lat, longitude: p1.point.lng },
            { latitude: p2.point.lat, longitude: p2.point.lng }, p2.accuracy || undefined);

        const seconds = getSeconds();
        const distance = getDistance();

        if (distance > 10000) return false;
        if (seconds < 1200 || distance < 1000) return true;
        return false;
    }


    // Helper function to cancel previous requests
    cancelPreviousRequests() {
        for (let i = 0; i < 60; i++) {
            defaultAgent.cancel(`replay_gps_${i}`);
        }
    }

    // Helper function to fetch data for a single request
    async fetchSingleRequest(reqParams, requestId) {
        if (Object.keys(this.egIdWiseList).length === 1) {
            const gps = await this.fetchSensorData({ ...reqParams, sensor: SENSOR_CGPS }, requestId);
            const egId = Object.keys(this.egIdWiseList)[0];
            if (gps && gps.hasOwnProperty(egId) && gps[egId].data && gps[egId].data.length > 0) {
                return gps;
            }
        }

        // Fallback to SENSOR_GPS if SENSOR_CGPS data is unavailable
        return this.fetchSensorData({ ...reqParams, sensor: SENSOR_GPS }, requestId);
    }

    async fetchGPS(params) {
        this.cancelPreviousRequests();

        const { startTime, endTime } = params;
        const diffMinutes = endTime.diff(startTime, "minutes");
        const reqCount = Math.ceil(diffMinutes / (this.sliceHours * 60));
        const results = [];
        const overallStart = moment();
        for (let i = 0; i < reqCount; i++) {
            const start = (i > 0) ? startTime.clone().add(i * this.sliceHours, 'hours') : startTime.clone();
            const end = (i < (reqCount - 1)) ? start.clone().add(this.sliceHours, 'hours') : endTime.clone();

            const requestId = `replay_gps_${i}`;
            const result = await this.fetchSingleRequest({
                reqIndex: i,
                startTime: start.format(),
                endTime: end.format()
            }, requestId);

            results.push(result);
        }
        const overallEnd = moment();
        const totalTime = overallEnd.diff(overallStart, "seconds");
        localStorage.setItem("totalAPIResponseTime", totalTime);
        this.combineGPSPoints(results);
        this.setPlayerTime();
    }




    fetchSpeedVariation() {
        return this.fetchSpeedVariationData().then(res => {
            this.egCategorySpeedVariation = res;
            this.emit(EVENT_SPEED_VARIATION, this.egCategorySpeedVariation);
            return this.egCategorySpeedVariation;
        });
    }

    fetchPTO(params) {
        const nonTruckIds = this._nonTruckEGIds();
        const requestId = `replay_${SENSOR_PTO}`;

        return (nonTruckIds.length > 0) ?
            this.fetchSensorData({
                ...params,
                startTime: params.startTime.format(), endTime: params.endTime.format(),
                egIds: nonTruckIds, sensor: SENSOR_PTO
            }, requestId).then(pto => this.combinePTOStatus(pto)) : Promise.resolve();
    }

    fetchBale(params) {
        const requestId = `replay_${SENSOR_BALE}`;
        return this.fetchSensorData({
            ...params, sensor: SENSOR_BALE
        }, requestId).then(data => this.combineBale(data));
    }

    fetchLoad(params) {
        this.loads = [];
        let isTruckLoad = false;
        let isRequest = (Object.keys(this.egIdWiseList).length > 0);
        let reqParams = {
            farm_uuid: this.farmId,
            crew_uuid: this.crewId ? this.crewId : undefined,
            region_uuid: this.regionId ? this.regionId : undefined,
            start_date: this._toApiDate(params.startTime),
            end_date: this._toApiDate(params.endTime),
            include_events: true
        };

        const egIds = Object.keys(this.egIdWiseList);
        if (isAutomaticLoad()) {
            isTruckLoad = true;
            if (egIds.length === 1) {
                const egItem = this.egIdWiseList[egIds[0]];
                const keyName = (egItem.isTruck || this.isEnableSeed) ? "target_equipment_group_uuid" : "source_equipment_group_uuid";
                reqParams = { ...reqParams, [keyName]: egItem.egId };
                isTruckLoad = egItem.isTruck;
            }
        } else if (egIds.length === 1) {
            const egItem = this.egIdWiseList[egIds[0]];
            const keyName = (egItem.isTruck || this.isEnableSeed) ? "target_equipment_group_uuid" : "source_equipment_group_uuid";
            reqParams = { ...reqParams, [keyName]: egItem.egId };
            isTruckLoad = egItem.isTruck;
        } else isTruckLoad = (egIds.length === this._truckEGIds().length);

        if (isTruckLoad && !isAutomaticLoad()) isRequest = false;

        if (isRequest) {
            const requestId = `replay_load`;
            const api = this.isEnableSeed ? '/api/seed/load/list' : '/api/equipment/group/load/list';

            return defaultAgent.get(api, reqParams, { requestId }).then(res => {
                const agentResult = apiResponse(res, []);
                if (agentResult.code === RECORD_FOUND.code) {
                    this.combineLoadV1(agentResult.data, isTruckLoad);
                    return true;
                }
                else if (agentResult.message) console.log(agentResult.message);
                return;
            }).catch(ex => {
                if (defaultAgent.isCancel(ex)) console.log(`Replay Load Request: `, ex.message);
                else console.log(ex.message);
                return;
            });
        } else return Promise.resolve(false);
    }

    fetchLoadV1(params) {
        this.loads = [];
        let isTruckLoad = false;
        let isRequest = (Object.keys(this.egIdWiseList).length > 0);
        let reqParams = {
            farm_uuid: this.farmId,
            crew_uuid: this.crewId ? this.crewId : undefined,
            region_uuid: this.regionId ? this.regionId : undefined,
            start_date: this._toApiDate(params.startTime),
            end_date: this._toApiDate(params.endTime),
            include_events: true
        };

        const egIds = Object.keys(this.egIdWiseList);
        if (egIds.length === 1) {
            const egItem = this.egIdWiseList[egIds[0]];
            isTruckLoad = egItem.isTruck;
        } else isTruckLoad = (egIds.length === this._truckEGIds().length);

        if (isRequest) {
            const requestId = `replay_load`;
            const api = this.isEnableSeed ? '/api/seed/load/list' : '/api/equipment/group/load/list';

            return defaultAgent.get(api, reqParams, { requestId }).then(res => {
                const agentResult = apiResponse(res, []);
                if (agentResult.code === RECORD_FOUND.code) {
                    this.combineLoadV1(agentResult.data, isTruckLoad);
                    return true;
                }
                else if (agentResult.message) console.log(agentResult.message);
                return;
            }).catch(ex => {
                if (defaultAgent.isCancel(ex)) console.log(`Replay Load Request: `, ex.message);
                else console.log(ex.message);
                return;
            });
        } else return Promise.resolve(false);
    }

    fetchSensorData({ sensor, startTime, endTime, egIds }, requestId) {
        const egId = egIds ? egIds.join(',') : Object.keys(this.egIdWiseList).join(',');
        const params = {
            equipment_group_uuid: egId,
            sensor,
            start_date: startTime,
            end_date: endTime
        };
        const start = moment().format(API_DATETIME_FORMAT);

        return defaultAgent.post('/api/equipment/group/sensor/data', params, { requestId }).then(res => {
            if (requestId === 0) {
                localStorage.removeItem("storeGps");
            }
            const agentResult = apiResponse(res, []);
            const end = moment().format(API_DATETIME_FORMAT);
            const diffTime = moment(end).diff(moment(start), "second");
            const previousStoredGps = JSON.parse(localStorage.getItem("storeGps")) || [];

            const storeGps = Array.isArray(previousStoredGps) ? [...previousStoredGps] : [];
            storeGps.push(diffTime);
            console.log(`🚀 ~ GPSPlayer ~ returndefaultAgent.post ~ diffTime:`, diffTime);

            localStorage.setItem("storeGps", JSON.stringify(storeGps));

            if (agentResult.code === RECORD_FOUND.code) return agentResult.data;
            else if (agentResult.message) console.log(agentResult.message);
            return [];
        }).catch(ex => {
            if (defaultAgent.isCancel(ex)) console.log(`Replay sensor Request of ${sensor}: `, ex.message);
            else console.log(ex.message);
        });
    }

    fetchSpeedVariationData() {
        const categoryIds = Object.keys(this.egIdWiseList).map(key => this.egIdWiseList[key].categoryId).join(',');
        const requestId = `replay_speedvariation`;

        return defaultAgent.get(`/api/equipment/speedvariation?equipment_category_id=${categoryIds}`, null, { requestId }).then(res => {
            const agentResult = apiResponse(res, []);
            if (agentResult.code === RECORD_FOUND.code) return agentResult.data;
            else if (agentResult.message) console.log(agentResult.message);
            return [];
        }).catch(ex => {
            if (defaultAgent.isCancel(ex)) console.log(`Replay speed variation Request of ${requestId}`, ex.message);
            else console.log(ex.message);
        });
    }

    fetchKPI(params) {
        const nonTruckIds = this._nonTruckEGIds();

        if (nonTruckIds.length > 0) {
            const reqParams = {
                start_date: params.startTime.format(),
                end_date: params.endTime.format(),
                equipment_group_uuid: nonTruckIds.join()
            };

            const requestId = `replay_kpi`;
            return defaultAgent.post('/api/equipment/group/kpi/data', reqParams, { requestId }).then(res => {
                const agentResult = apiResponse(res, []);
                if (agentResult.code === RECORD_FOUND.code) return this.combineKPI(agentResult.data);
                else if (agentResult.message) console.log(agentResult.message);
                return true;
            }).catch(ex => {
                if (defaultAgent.isCancel(ex)) console.log(`Replay KPI Request: `, ex.message);
                else console.log(ex.message);
            });
        } else return Promise.resolve(false);
    }

    combineGPSPoints(response) {
        this.gpsPoints = {};

        const speedList = [], isSpeed = (Object.keys(this.egIdWiseList).length === 1);

        for (const gpsList of response) {
            for (const egId in gpsList) {
                if (!this.egIdWiseList[egId]) return;
                if (!(gpsList[egId].hasOwnProperty("data")
                    && Array.isArray(gpsList[egId].data)
                    && gpsList[egId].data.length > 0)) continue;

                for (const item of gpsList[egId].data) {
                    if (!this.gpsPoints[egId]) this.gpsPoints[egId] = [];
                    item.speed = toMilesPerHour(item.speed);
                    this.gpsPoints[egId].push({
                        egId, ts: item.ts,
                        point: { lat: item.loc_point[1], lng: item.loc_point[0] },
                        loc_point: item.loc_point,
                        accuracy: item.hasOwnProperty("h_accuracy") ? item.h_accuracy : null,
                        speed: item.speed,
                        polylineIndex: null,
                    });

                    if (isSpeed && item.hasOwnProperty("speed")) speedList.push(item.speed || 0);
                }
            }
        }

        for (const egId in this.gpsPoints) {
            const gpsList = this.gpsPoints[egId]
                .sort((a, b) => a.ts - b.ts)
                .map((e, i) => ({ ...e, index: i }));
            const gpsListWith = [];

            for (let i = 0; i < gpsList.length; i++) {
                const curr = gpsList[i];
                const prev = i > 0 ? gpsList[i - 1] : null;
                const distance = prev ? getPreciseDistance(
                    { latitude: prev.point.lat, longitude: prev.point.lng },
                    { latitude: curr.point.lat, longitude: curr.point.lng }, curr.accuracy || undefined) : 0;
                gpsListWith.push({ ...curr, distance });
            }
            this.gpsPoints[egId] = gpsListWith;
        }
        // Find thr avg speed and max speed
        if (isSpeed && speedList.length > 0) {
            const sumOfSpeed = speedList.reduce((sum, s) => s + sum, 0);
            const avgSpeed = (sumOfSpeed / speedList.length);
            const maxSpeed = Math.max(...speedList);
            this.emit(EVENT_KPI, { avgSpeed, maxSpeed });
        }
        return this;
    }

    calculateDistance(p1, p2) {
        return getPreciseDistance(
            { latitude: p1.point.lat, longitude: p1.point.lng },
            { latitude: p2.point.lat, longitude: p2.point.lng }, p2.accuracy || undefined);
    }

    combinePTOStatus(ptoList) {
        this.ptoSlot = {};
        for (const egId in ptoList) {
            if (!this.egIdWiseList[egId]) return;
            if (!(ptoList[egId].hasOwnProperty("data") && Array.isArray(ptoList[egId].data)
                && ptoList[egId].data.length > 0)) continue;

            const egSlot = [];
            const egPTO = ptoList[egId].data.sort((a, b) => a.ts - b.ts);
            for (const item of egPTO) {
                const lastSlot = (egSlot.length > 0) ? egSlot[egSlot.length - 1] : null;
                if (!lastSlot && item.state)
                    egSlot.push({ start: item.ts, end: null, state: item.state });
                else if (lastSlot && !item.state)
                    egSlot[egSlot.length - 1] = { ...lastSlot, end: item.ts, state: item.state };
                else if (lastSlot && !item.state && lastSlot.state === item.state)
                    egSlot[egSlot.length - 1] = { ...lastSlot, end: item.ts };
                else if (lastSlot && item.state && lastSlot.state !== item.state)
                    egSlot.push({ start: item.ts, end: null, state: item.state });
            }

            if (egSlot.length > 0) {
                if (!egSlot[egSlot.length - 1].end) egSlot[egSlot.length - 1].end = egPTO[egPTO.length - 1].ts;
                this.ptoSlot[egId] = egSlot;
            }
        }

        for (const egId in this.gpsPoints) {
            if (this.egIdWiseList[egId].isTruck) continue;

            const egSlot = this.ptoSlot.hasOwnProperty(egId) ? this.ptoSlot[egId] : null;
            for (const gps of this.gpsPoints[egId])
                gps.pto = egSlot ? egSlot.some(e => e.start <= gps.ts && e.end >= gps.ts) : false;
        }

        return this;
    }

    combineBale(dataList) {
        this.bales = {};
        const egIdWiseBale = {};
        const baleMapping = {};

        for (const id in dataList) {
            if (!Array.isArray(dataList[id].data)) continue;
            const data = dataList[id].data
                .filter(e => e.hasOwnProperty("bale_start_ts") && e.hasOwnProperty("bale_finished_ts"))
                .sort((a, b) => parseInt(a.seq_num) - parseInt(b.seq_num));

            egIdWiseBale[id] = data.map(e => ({
                seqNumber: parseInt(e.seq_num),
                baleWeight: e.hasOwnProperty("lbs") ? this.unit.toRoundOfWeight(e.lbs) : null,
                baleStartTS: e.bale_start_ts,
                baleFinishedTS: e.bale_finished_ts,
                baleDropBeginTS: e.bale_drop_begin_ts ? e.bale_drop_begin_ts : null,
                flakeCount: e.flake_count
            }));
        }

        for (const id in this.gpsPoints) {
            for (const gps of this.gpsPoints[id]) {
                const foundBales = egIdWiseBale[id]
                    .filter(e => (e.baleStartTS <= gps.ts && e.baleFinishedTS >= gps.ts));
                if (foundBales.length === 0) continue;

                const baleId = foundBales[0].seqNumber;
                if (!this.bales[baleId]) this.bales[baleId] = foundBales[0];

                if (!baleMapping[id]) baleMapping[id] = {};
                //if (baleMapping[id][baleId]) continue;
                baleMapping[id][baleId] = { gpsIndex: gps.index, seqNumber: foundBales[0].seqNumber };
            }
        }

        for (const id in baleMapping) {
            const mapping = baleMapping[id];
            const baleList = Object.keys(mapping)
                .map(baleId => ({ baleId, ...mapping[baleId] }))
                .sort((a, b) => a.seqNumber - b.seqNumber);

            let baleNo = 0;
            for (const item of baleList) {
                baleNo++;
                this.gpsPoints[id][item.gpsIndex].bale = { baleId: item.baleId, baleNo };
            }
        }

        this.emit(EVENT_KPI, { bales: Object.keys(this.bales).length });
        return this.bales;
    }

    combineLoad(load, isTruckLoad) {
        this.loads = [];

        let loadList = [];
        const keyName = (isTruckLoad || this.isEnableSeed) ? "target_equipment_group_uuid" : "source_equipment_group_uuid";
        for (const item of load) {
            const egId = item[keyName];
            if (!this.egIdWiseList[egId]) continue;
            if (isTruckLoad && (item.anonymous_eg === 1)) continue;

            const loadType = this.egIdWiseList[egId].crews && this.egIdWiseList[egId].crews.length > 0
                && this.egIdWiseList[egId].crews[0].load_type ? this.egIdWiseList[egId].crews[0].load_type
                : LOAD_TYPE.MANUAL;
            const isAutomatic = loadType === LOAD_TYPE.AUTOMATIC;

            const stopedLocation = item.events.filter(e => e.event_status === (isAutomatic ? 1 : 4));
            // const stopedLocation = item.events.filter(e => e.event_status === (isAutomaticLoad() ? 1 : 4));

            const egData = this.mapEGList.filter(eg => {
                if (eg.egId === item['source_equipment_group_uuid']) return true;
                else return false
            });

            const pathColor = (egData && egData.length > 0) ? egData[0].color : '#000';
            const loadTsArr = [...this.loadTs, { ts: moment.utc(item.end_date).valueOf(), color: pathColor }];

            this.loadTs = loadTsArr.sort((a, b) => a.ts - b.ts);

            loadList.push({
                egId,
                loadId: item.load_activity_uuid,
                loadNo: item.load_no,
                crewName: item.crew_name,
                sourceEGName: item.source_equipment_group_name,
                sourceEGId: item.source_equipment_group_uuid,
                targetEGName: item.target_equipment_group_name,
                targetEGId: item.target_equipment_group_uuid,
                pathColor: pathColor,
                fieldName: item.field_geo_region_name,
                fieldShapeName: item.field_geo_shape_name,
                anonymousEG: (item.anonymous_eg === 1),
                startTime: item.start_date,
                endTime: item.end_date,
                ts: moment.utc(item.end_date).valueOf(),
                loadSeconds: item.load_seconds,
                loadWeight: item.load_capacity,
                location: stopedLocation.length > 0
                    && stopedLocation[0].latitude
                    && stopedLocation[0].longitude ? {
                    lat: stopedLocation[0].latitude,
                    lng: stopedLocation[0].longitude
                } : null
            });
        }

        const nonGPSLoadList = loadList.filter(e => !e.location);
        if (nonGPSLoadList.length > 0) {
            const loadLocation = this.findGPSLocationForLoad(nonGPSLoadList);
            loadList = loadList.map(e => ({
                ...e,
                location: loadLocation[e.loadId] ? loadLocation[e.loadId].location : e.location ? e.location : null
            }));
        }

        const loadNoById = {};
        loadList = loadList
            .sort((a, b) => a.ts - b.ts)
            .map(e => {
                if (!loadNoById[e.egId]) loadNoById[e.egId] = 0;
                loadNoById[e.egId]++;
                return { ...e, loadNo: loadNoById[e.egId] };
            });

        this.loads = loadList;
        this.emit(EVENT_KPI, { loads: this.loads.length });
        return this.loads;
    }

    processItemSequence(item) {
        const loadNoMap = new Map();
        let loads = [];
        const egIds = this._nonTruckEGIds();
        const sequence = item.load_sequence.filter((e) => egIds.includes(e.target_equipment_group_uuid));
        sequence.forEach(e => {
            const loadNo = (loadNoMap.get(e.target_equipment_group_uuid) || 0) + 1;
            loadNoMap.set(e.target_equipment_group_uuid, loadNo);
            loads.push({
                "egId": e.target_equipment_group_uuid,
                "loadId": e.sequence_id,
                "loadNo": loadNo,
                "crewName": item.crew_name,
                "sourceEGName": item.source_equipment_group_name,
                "sourceEGId": item.source_equipment_group_uuid,
                "targetEGName": e.target_equipment_group_name,
                "targetEGId": e.target_equipment_group_uuid,
                "pathColor": null,
                "fieldName": e.geo_region_name,
                "fieldShapeName": e.geo_shape_name,
                "anonymousEG": (item.anonymous_eg === 1),
                "startTime": moment.utc(e.created_date).valueOf(),
                "endTime": moment.utc(e.created_date).valueOf(),
                "ts": moment.utc(e.created_date).valueOf(),
                "load_date": moment.utc(e.created_date_tz).valueOf(),
                "loadSeconds": 0,
                "loadWeight": e.load_capacity,
                "location": null,
                "isSequenceLoad": true,
                "loadActivityId": e.load_activity_uuid
            })
        });
        return loads;
    }

    processSequenceLoadNo(sequenceLoadList) {
        let updatedList = [];
        const byEGData = groupBy('egId')(sequenceLoadList.sort((a, b) => a.ts - b.ts));
        Object.keys(byEGData).forEach((egId) => {
            const byActivityId = groupBy('loadActivityId')(byEGData[egId]);
            Object.keys(byActivityId).forEach((activityId, index) => {
                const loadData = byActivityId[activityId];
                const isSingleLoad = (loadData.length === 1) ? true : false;
                const data = loadData.map((e) => {
                    return {
                        ...e,
                        loadNo: isSingleLoad ? (index + 1) : `${index + 1}${this.convertNumberToLetter(e.loadNo)}`
                    }
                });
                updatedList = [...updatedList, ...data];
            })
        });
        return updatedList;
    }

    convertNumberToLetter(number) {
        if (number < 1 || number > 26) return number;
        const letter = String.fromCharCode(number + 96);
        return letter;
    }

    combineLoadV1(load, isTruckLoad) {
        let loadList = [];
        //let savedDistances = this.fieldDistance && this.fieldDistance.length > 0 ? this.fieldDistance : [];
        let keyName = (isTruckLoad || this.isEnableSeed) ? "target_equipment_group_uuid" : "source_equipment_group_uuid";
        for (const item of load) {
            let egId = item[keyName];
            let egKeyName = keyName;
            if (isTruckLoad && item.transfer_type === 2) {
                if (keyName === "target_equipment_group_uuid") {
                    egId = item["source_equipment_group_uuid"];
                    egKeyName = "source_equipment_group_uuid";
                }
            }
            if (!isTruckLoad && !item.source_equipment_group_uuid) {
                if (keyName === "source_equipment_group_uuid") {
                    egId = item["target_equipment_group_uuid"];
                    egKeyName = "target_equipment_group_uuid";
                }
            }

            // for planter load
            if (this.isEnableSeed && item.load_sequence && item.load_sequence.length > 0) {
                const seq_load = this.processItemSequence(item);
                loadList = [...loadList, ...seq_load];
            }
            /***************************************/

            if (!this.egIdWiseList[egId]) continue;
            if (isTruckLoad && (item.anonymous_eg === 1)) continue;

            const loadType = this.egIdWiseList[egId].crews && this.egIdWiseList[egId].crews.length > 0
                && this.egIdWiseList[egId].crews[0].load_type ? this.egIdWiseList[egId].crews[0].load_type
                : LOAD_TYPE.MANUAL;
            const isAutomatic = loadType === LOAD_TYPE.AUTOMATIC;

            const stopedLocation = item.events.filter(e => e.event_status === (isAutomatic ? 1 : 4));
            // const stopedLocation = item.events.filter(e => e.event_status === (isAutomaticLoad() ? 1 : 4));

            const egData = this.mapEGList.filter(eg => {
                //'source_equipment_group_uuid'
                if (eg.egId === item[egKeyName]) return true;
                else return false
            });

            const pathColor = (egData && egData.length > 0) ? egData[0].color : '#000';
            const loadTsArr = [...this.loadTs, { ts: moment.utc(item.end_date).valueOf(), color: pathColor }];

            this.loadTs = loadTsArr.sort((a, b) => a.ts - b.ts);

            const predictedDistance = (item.distance_info && item.distance_info.distance) ? this.unit.toFixedOf(item.distance_info.distance, 2) : 0;
            const actualDistance = (item.distance_info && item.distance_info.travelled_distance) ? this.unit.toFixedOf(item.distance_info.travelled_distance, 2) : 0

            //const actualDistance = (isTruckLoad) ? this.getActualLoadDistance({ egId, startTs: moment.utc(item.start_date).valueOf(), endTs: moment.utc(item.end_date).valueOf(), isAutomaticLoad: (item.load_type === 1), load: item }) : { distance: 0 };
            //const predictedDistance = isTruckLoad ? getDistanceValue(item.field_geo_shape_uuid, item.offload_geo_shape_uuid, savedDistances) : 0;

            loadList.push({
                egId,
                loadId: item.load_activity_uuid,
                loadNo: item.load_no,
                crewName: item.crew_name,
                sourceEGName: item.source_equipment_group_name,
                sourceEGId: item.source_equipment_group_uuid,
                targetEGName: item.target_equipment_group_name,
                targetEGId: item.target_equipment_group_uuid,
                pathColor: pathColor,
                fieldName: item.field_geo_region_name,
                fieldShapeName: item.field_geo_shape_name,
                anonymousEG: (item.anonymous_eg === 1),
                startTime: item.start_date,
                endTime: item.end_date,
                ts: moment.utc(item.load_date).valueOf(),
                loadSeconds: item.load_seconds,
                loadWeight: item.load_capacity,
                location: stopedLocation.length > 0
                    && stopedLocation[0].latitude
                    && stopedLocation[0].longitude ? {
                    lat: stopedLocation[0].latitude,
                    lng: stopedLocation[0].longitude
                } : null,
                loadType: item.load_type,
                transferType: item.transfer_type,
                actualDistance: actualDistance,
                predictedDistance: predictedDistance
            });
        }
        const nonGPSLoadList = loadList.filter(e => !e.location);
        if (nonGPSLoadList.length > 0) {
            const loadLocation = this.findGPSLocationForLoad(nonGPSLoadList);
            loadList = loadList.map(e => ({
                ...e,
                location: loadLocation[e.loadId] ? loadLocation[e.loadId].location : e.location ? e.location : null
            }));
        }

        // for planter load
        const sequenceLoadList = loadList.filter((e) => e.isSequenceLoad);
        const nonSequenceLoadList = loadList.filter((e) => !e.isSequenceLoad);

        if (sequenceLoadList && sequenceLoadList.length > 0) {
            const updatedSequenceList = this.processSequenceLoadNo(sequenceLoadList);
            loadList = [...nonSequenceLoadList, ...updatedSequenceList]
        }
        /***************************************/
        const loadNoById = {};
        loadList = loadList
            .sort((a, b) => a.ts - b.ts)
            .map(e => {
                if (!loadNoById[e.egId]) loadNoById[e.egId] = 0;
                loadNoById[e.egId]++;
                return { ...e, loadNo: e.isSequenceLoad ? e.loadNo : loadNoById[e.egId] };
            });

        //Load filtered based on source and target selections
        //Displaying the load for source's corresponding targetEg selections
        let filteredLoads = [];
        const truckIds = this._truckEGIds();
        if (truckIds.length > 0) {
            truckIds.forEach(truckId => {
                loadList.forEach(load => {
                    if ((load.transferType === 2 && load.sourceEGId === truckId) || load.targetEGId === truckId) {
                        filteredLoads.push(load);
                    }
                })
            })
        } else {
            filteredLoads = [...loadList]
        }
        this.loads = filteredLoads;
        this.emit(EVENT_KPI, { loads: this.loads.length });
        return this.loads;
    }

    findGPSLocationForLoad(loadList) {
        const loadLocation = {};
        const egLoad = {};

        for (const load of loadList) {
            if (!egLoad[load.egId]) egLoad[load.egId] = [];
            egLoad[load.egId].push({
                loadId: load.loadId,
                start: moment.utc(load.startTime).valueOf(),
                end: moment.utc(load.endTime).valueOf()
            });
        }

        for (const egId in this.gpsPoints) {
            if (!egLoad.hasOwnProperty(egId)) continue;

            const loads = egLoad[egId];
            for (const gps of this.gpsPoints[egId]) {
                const foundLoads = loads.filter(e => e.start <= gps.ts && e.end >= gps.ts);
                if (foundLoads.length === 0) continue;

                const { loadId } = foundLoads[0];
                loadLocation[loadId] = { location: gps.point };
            }
        }

        const diffMSeconds = (1 * 60 * 60 * 1000);
        for (const egId in this.gpsPoints) {
            if (!egLoad.hasOwnProperty(egId)) continue;

            const loads = egLoad[egId].filter(e => !loadLocation[e.loadId]);
            for (const gps of this.gpsPoints[egId]) {
                const foundLoads = loads
                    .map(e => ({ ...e, closet: (gps.ts - e.start) }))
                    .filter(e => gps.ts >= e.start && e.closet <= diffMSeconds)
                    .sort((a, b) => a.closet - b.closet);
                if (foundLoads.length === 0) continue;

                for (const load of foundLoads) {
                    if (loadLocation[load.loadId]) continue;
                    loadLocation[load.loadId] = { location: gps.point };
                }
            }
        }

        return loadLocation;
    }

    combineKPI = message => {
        let acres = 0;
        for (const key in message) {
            if (!(message[key].data && Array.isArray(message[key].data))) continue;
            const data = message[key].data;
            for (const item of data) {
                acres += item.summary && item.summary.work && !isNaN(item.summary.work.acres) ? item.summary.work.acres : 0;
            }
        }

        this.emit(EVENT_KPI, { acres });
    };

    enableSpeedVariation(enable, redraw) {
        this.isEnableSpeedVariation = enable;
        if (!redraw) return;
        const drawSpeedVariation = () => {
            this.emit(SPEED_VARIATION_FETCHED);
            this.clearPaths();
            this.clearPathReference();
            if (this.progressTime) this.draw(this.playerStartTime, this.progressTime);
        };
        if (enable) {
            this.emit(SPEED_VARIATION_FETCHING);
            this.fetchSpeedVariation().then(drawSpeedVariation).catch(console.log);
        }
        else drawSpeedVariation();
    }

    enablePTOPaths(enable) {
        for (const egId in this.mapObjects) {
            for (const item of this.mapObjects[egId].polylines) {
                if (enable && item.lineType === 2)
                    item.polyline.setOptions({ strokeOpacity: 0, icons: [this._dottedLine()] });
                else
                    item.polyline.setOptions({ strokeOpacity: 1, icons: null });
            }
        }
        return this;
    }

    enablePTO(enable) {
        this.isEnablePTO = enable;

        if (!enable || Object.keys(this.gpsPoints).length === 0) {
            this.clearPTOSwitchPoints();
            return this.enablePTOPaths(enable);
        }

        if (!this.ptoSlot || Object.keys(this.ptoSlot).length === 0) {
            const fetched = () => {
                this.emit(PTO_FETCHED);
                if (isPlaying) this.play();
            };

            const isPlaying = this.isPlaying;
            this.emit(PTO_FETCHING);
            this.pause();
            this.fetchPTO({ startTime: this.startUTCDate, endTime: this.endUTCDate }).then(() => {
                this.clearPathReference();
                this.clearPaths(true);
                if (this.progressTime) this.draw(this.playerStartTime, this.progressTime);
                fetched();
            }).catch(fetched);
        } else {
            this.enablePTOPaths(true);

            // Put the PTO switch points
            if (!this.progressTime) return;
            let findPoints = [];
            for (const egId in this.gpsPoints) {
                findPoints = findPoints.concat(this.gpsPoints[egId].filter(e => e.ts <= this.progressTime));
            }
            findPoints.sort((a, b) => a.ts - b.ts);

            for (const curr of findPoints) {
                const prev = (curr.index > 0) ? this.gpsPoints[curr.egId][curr.index - 1] : null;
                if (prev) this.putPTOSwitchPoint(prev, curr);
            }
        }
    }

    enableBale(enable) {
        const bales = enable ? Object.keys(this.bales).length : 0;
        this.emit(EVENT_KPI, { bales });

        if (!enable || Object.keys(this.gpsPoints).length === 0) {
            this.isEnableBale = enable;
            return this.clearBales();
        }

        const putBaleCount = () => {
            this.isEnableBale = enable;
            if (!this.progressTime) return;

            let findPoints = [];
            for (const egId in this.gpsPoints) {
                findPoints = findPoints.concat(this.gpsPoints[egId].filter(e => e.bale && e.ts <= this.progressTime));
            }
            findPoints.sort((a, b) => a.ts - b.ts);
            for (const item of findPoints) this.putBale(item.egId, item);
        };

        if (!this.bales || Object.keys(this.bales).length === 0) {
            const fetched = () => {
                this.emit(BALE_FETCHED);
                if (isPlaying) this.play();
                this.isEnableBale = enable;
            };

            const isPlaying = this.isPlaying;
            this.emit(BALE_FETCHING);
            this.pause();
            this.fetchBale({ startTime: this.startUTCDate, endTime: this.endUTCDate }).then(() => {
                putBaleCount();
                fetched();
            }).catch(fetched);
        } else putBaleCount();
    }

    enableSeed(isSeedEnable) {
        this.isEnableSeed = isSeedEnable;
        return this;
    }

    enableLoad(enable) {
        const loads = enable ? this.loads.length : 0;
        this.emit(EVENT_KPI, { loads });

        if (!enable || Object.keys(this.gpsPoints).length === 0) {
            this.isEnableLoad = enable;
            return this.clearLoads();
        }
        const putLoadCount = () => {
            this.isEnableLoad = enable;
            if (!this.progressTime) return;
            // this.putLoadV1();
        };

        if (!this.loads || this.loads.length === 0) {
            const fetched = () => {
                this.emit(LOAD_FETCHED);
                if (isPlaying) this.play();
                this.isEnableLoad = enable;
            };

            const isPlaying = this.isPlaying;
            this.emit(LOAD_FETCHING);
            this.pause();
            this.fetchLoadV1({ startTime: this.startDate, endTime: this.endDate }).then(() => {
                putLoadCount();
                fetched();
            }).catch(fetched);
        } else putLoadCount();
    }

    enableArea(enable) {
        this.isEnableArea = enable;
    }

    _nonTruckEGIds() {
        const nonTruckIds = [];
        for (const egId in this.egIdWiseList) {
            if (!this.egIdWiseList[egId].isTruck) nonTruckIds.push(egId);
        }
        return nonTruckIds;
    }

    _truckEGIds() {
        const truckIds = [];
        for (const egId in this.egIdWiseList) {
            if (this.egIdWiseList[egId].isTruck) truckIds.push(egId);
        }
        return truckIds;
    }

    _toApiDate(date) {
        return date.format(`${API_DATE_FORMAT} ${API_TIME_FORMAT}`);
    }

    setPlayerTime() {
        const times = [];
        for (const egId in this.gpsPoints) {
            const points = this.gpsPoints[egId];
            if (points.length === 0) continue;
            times.push(points[0].ts);
            times.push(points[points.length - 1].ts);
        }

        if (times.length > 0) {
            times.sort((a, b) => a - b);
            this.playerStartTime = times[0];
            this.playerEndTime = times[times.length - 1];
            this.progressTime = this.playerStartTime;
        }

        return this;
    }

    setOptions(options) {
        this.options = { ...this.options, ...options };
        return this;
    }

    setMapEG(EGList) {
        this.mapEGList = [...this.mapEGList, ...EGList];
        return this;
    }

    setFieldDistance(data) {
        this.fieldDistance = [...this.fieldDistance, ...data];
        return this;
    }

    clearOptions() {
        this.options = { ...this.defaultOptions };
        return this;
    }

    getGPSPoints() {
        return this.gpsPoints;
    }

    getEgItem(egId) {
        return this.egIdWiseList && this.egIdWiseList.hasOwnProperty(egId) ? this.egIdWiseList[egId] : null;
    }

    clearPathReference() {
        for (const egId in this.gpsPoints) {
            for (const point of this.gpsPoints[egId]) point.polylineIndex = null;
        }
    }

    clearPaths(isRemoveObject = false) {
        for (const egId in this.mapObjects) {
            for (const item of this.mapObjects[egId].polylines) {
                item.polyline.setPath([]);
                item.polyline.setMap(null);
            }
            if (isRemoveObject) this.mapObjects[egId].polylines.length = 0;
        }
        return this;
    }

    clearPTOSwitchPoints(isRemoveObject = false) {
        for (const egId in this.mapObjects) {
            if (!this.mapObjects[egId].ptoSwitchPoints) continue;
            for (const gpsIndex in this.mapObjects[egId].ptoSwitchPoints) {
                const marker = this.mapObjects[egId].ptoSwitchPoints[gpsIndex].marker;
                if (marker) marker.setMap(null);
            }
            if (isRemoveObject) delete this.mapObjects[egId].ptoSwitchPoints;
        }
        return this;
    }

    clearMarkers(isRemoveObject = false) {
        for (const egId in this.mapObjects) {
            if (this.mapObjects[egId].marker) {
                this.mapObjects[egId].marker.setMap(null);
                if (isRemoveObject) this.mapObjects[egId].marker = null;
            }
        }
        return this;
    }

    clearLoads(isRemoveObject = false) {
        for (const egId in this.mapObjects) {
            if (!this.mapObjects[egId].loads) continue;
            for (const loadId in this.mapObjects[egId].loads) {
                const marker = this.mapObjects[egId].loads[loadId].marker;
                if (marker) marker.setMap(null);
            }
            if (isRemoveObject) delete this.mapObjects[egId].loads;
        }
        return this;
    }

    clearBales(isRemoveObject = false) {
        for (const egId in this.mapObjects) {
            if (!this.mapObjects[egId].bales) continue;
            for (const baleId in this.mapObjects[egId].bales) {
                const marker = this.mapObjects[egId].bales[baleId].marker;
                if (marker) marker.setMap(null);
            }
            if (isRemoveObject) delete this.mapObjects[egId].bales;
        }
        return this;
    }

    getActualLoadDistance(distData) {
        const { egId, startTs, endTs, isAutomaticLoad, load } = distData;
        const { load_activity_location
            : loadActivityLocations, offload_geo_shape_uuid, field_geo_shape_uuid } = load;
        if (egId && this.gpsPoints && this.gpsPoints[egId]) {
            let egGps = this.gpsPoints[egId];

            if (isAutomaticLoad) {
                if (loadActivityLocations && loadActivityLocations.length > 0) {
                    const locations = loadActivityLocations;
                    const enterEvents = locations.filter((loc) => loc.location_change_type === 'enter' && loc.geo_shape_uuid === field_geo_shape_uuid);
                    const exitEvents = locations.filter((loc) => loc.location_change_type === 'exit' && loc.geo_shape_uuid === offload_geo_shape_uuid);

                    const leastRecentEnter = enterEvents.reduce((earliest, current) => {
                        return new Date(current.event_date) < new Date(earliest.event_date) ? current : earliest;
                    }, enterEvents[0]);

                    const mostRecentExit = exitEvents.reduce((latest, current) => {
                        return new Date(current.event_date) > new Date(latest.event_date) ? current : latest;
                    }, exitEvents[0]);

                    egGps = egGps.filter((s) => s.ts > moment(leastRecentEnter.event_date).valueOf() && s.ts < moment(mostRecentExit.event_date).valueOf()).sort((a, b) => b.ts - a.ts)

                } else {
                    egGps = egGps.filter((e) => e.ts > startTs && e.ts < endTs)
                }
            } else {
                const { events } = load;

                const startEvent = events.find((e) => e.event_status === 1);
                let endEvent = events.find((e) => e.event_status === 6);
                if (!endEvent) {
                    return { distance: 0 };
                }

                if (startEvent && startEvent.event_date && endEvent && endEvent.event_date) {
                    egGps = egGps.filter((s) => s.ts > moment(startEvent.event_date).valueOf() && s.ts < moment(endEvent.event_date).valueOf());
                } else {
                    egGps = [];
                }
            }

            if (egGps && egGps.length > 0) {
                let i = 0, totalDistance = 0;
                for (i; i < egGps.length; i++) {
                    const prePoint = egGps[i];
                    const nextPoint = egGps[i + 1] ? egGps[i + 1] : egGps[egGps.length - 1];
                    let distance = getPreciseDistance(prePoint.loc_point, nextPoint.loc_point);
                    totalDistance += distance;
                    i += 1
                }
                return { distance: this.unit.toRoundOfDistance((totalDistance * 0.000621371192)) };
            }

        }
        return { distance: 0 };
    }

    clearTimer() {
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
        return this;
    }

    reset() {
        if (this.isPlaying) this.stop();
        else this.clearTimer();
        this.clearMarkers();
        this.clearLoads();
        this.clearBales();
        this.clearPTOSwitchPoints();
        this.clearPaths();

        this.leftDuration = 0;
        this.isPlaying = false;
        return this;
    }

    clear() {
        this.clearTimer();
        this.clearMarkers();
        this.clearLoads(true);
        this.clearBales(true);
        this.clearPTOSwitchPoints(true);
        this.clearPaths(true);

        this.egIdWiseList = {};
        this.gpsPoints = {};
        this.ptoSlot = {};
        this.loads = [];
        this.bales = {};
        this.egCategorySpeedVariation = [];
        this.startDate = null;
        this.endDate = null;
        this.playerStartTime = null;
        this.playerEndTime = null;
        this.progressTime = null;
        this.leftDuration = 0;
        this.isPlaying = false;
        return this;
    }

    clearStream() {
        this.clear();
        this.emit(EVENT_CLEAR_STREAM);
    }

    dispose() {
        this.clear();
        this.mapObjects = {};
        this.callbacks = {};
    }

    addListener(...agrs) {
        super.addListener(...agrs);
        return this;
    }
}
